import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from '@mui/material';
import axios from 'axios';
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import './RuntimeValidation.css';

// Constants
const STATUS = {
  PASS: 'Pass',
  FAIL: 'Fail',
  RUNNING: 'Running',
  NOT_APPLICABLE: 'Not Applicable',
};

// Module Definitions
const MODULES = [
  { id: 0, name: 'All Modules', icon: '🔍' },
  { id: 2, name: 'Entity & Sanctions Screening', icon: '🛡️' },
  { id: 3, name: 'Trade Document Verification', icon: '📄' },
  { id: 4, name: 'Price & Product Validation', icon: '💰' },
  { id: 5, name: 'Vessel & Container Tracking', icon: '🚢' },
  { id: 7, name: 'AI Risk Scoring & Alerts', icon: '🤖' },
  { id: 8, name: 'Compliance & SAR Reporting', icon: '⚖️' },
];

// Use environment variable for API URL and validate
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error('API_BASE_URL is not defined in .env');
}

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box className="rtv-error-container">
          <Typography variant="h6">
            Something went wrong: {this.state.error?.message || 'Unknown error'}
          </Typography>
          <Button onClick={() => window.location.reload()} variant="contained">
            Reload Page
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const RuntimeValidation = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const transactionId = location.state?.transactionId || 'TX123';
  const transactionName = location.state?.transactionName || `Transaction ${transactionId}`;
  const module = parseInt(new URLSearchParams(location.search).get('module'), 10) || 0;
  const validations = location.state?.validations || [];

  // Map validation check names to module IDs based on compliance_validation_tasks and document_validation_tasks
  const getModuleId = (checkName) => {
    const normalizedCheckName = checkName?.toLowerCase().trim();
    
    // Compliance & SAR Reporting (Module 8)
    const complianceChecks = [
      'export country check',
      'high risk countries',
      'dual use goods check',
      'assessed vs declared check'
    ];
    
    // Trade Document Verification (Module 3)
    const documentChecks = [
      'unusual documents check',
      'container number validation',
      'missing important details check',
      'invoice proforma check',
      'air waybill check',
      'bill of lading uniqueness',
      'missing documents check',
      'all goods description check',
      'hs code consistency check',
      'trading partners consistency check'
    ];
    
    // Price & Product Validation (Module 4)
    const priceProductChecks = [
      'abnormal shipping size'
    ];
    
    // Vessel & Container Tracking (Module 5)
    const vesselContainerChecks = [
      // Container Number Validation is in document checks
    ];
    
    // Check which module the validation belongs to
    if (complianceChecks.includes(normalizedCheckName)) {
      return 8; // Compliance & SAR Reporting
    } else if (documentChecks.includes(normalizedCheckName)) {
      return 3; // Trade Document Verification
    } else if (priceProductChecks.includes(normalizedCheckName)) {
      return 4; // Price & Product Validation
    } else if (vesselContainerChecks.includes(normalizedCheckName)) {
      return 5; // Vessel & Container Tracking
    } else {
      return 7; // AI Risk Scoring & Alerts (default for unmatched)
    }
  };

  // State Management
  const [allValidations, setValidations] = useState(
    validations.map((v) => ({
      ...v,
      status: v.status === 'PASSED' 
        ? STATUS.PASS 
        : v.status === 'FAILED' 
        ? STATUS.FAIL 
        : v.status === 'NOT_APPLICABLE' || v.status === 'Not Applicable'
        ? STATUS.NOT_APPLICABLE
        : v.status || STATUS.RUNNING,
      module: v.module || getModuleId(v.checkName),
    }))
  );
  const [selectedValidation, setSelectedValidation] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Set selected module from query params
  React.useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const moduleParam = queryParams.get('module');
    const moduleId = moduleParam ? parseInt(moduleParam, 10) : 0;
    
    if (!isNaN(moduleId) && MODULES.some((m) => m.id === moduleId)) {
      setSelectedModule(moduleId);
    } else {
      setSelectedModule(0);
    }
    
    // Log for debugging
    console.log('Current module:', moduleId);
    console.log('All validation check names:', allValidations.map(v => ({
      name: v.checkName,
      normalized: v.checkName?.toLowerCase().trim(),
      assignedModule: v.module
    })));
    console.log('Validations by module:', allValidations.reduce((acc, v) => {
      acc[v.module] = acc[v.module] || [];
      acc[v.module].push(v.checkName);
      return acc;
    }, {}));
  }, [location.search, allValidations]);

  const handleModuleChange = (event, newValue) => {
    setSelectedModule(newValue);
    setPage(0);
    navigate(`/runtime-validation?transactionId=${transactionId}&module=${newValue}`, {
      state: { validations, transactionId, transactionName },
    });
  };

  // Fetch documents for a transaction
  const fetchDocuments = async (transactionId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(`${API_BASE_URL}/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const transaction = response.data.find((t) => t.id.toString() === transactionId.toString());
      if (!transaction) throw new Error('Transaction not found.');
      return transaction.documents || [];
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch documents.');
    }
  };

  const handleOpenDetails = (validation) => {
    setSelectedValidation(validation);
    setOpenDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsModal(false);
    setSelectedValidation(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter and search logic
  const filteredValidations = useMemo(() => {
    let filtered = allValidations;

    // Filter by module
    if (selectedModule && selectedModule !== 0) {
      filtered = filtered.filter((v) => v.module === selectedModule);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter((v) => v.status?.toLowerCase() === statusFilter);
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter((v) =>
        v.checkName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [allValidations, selectedModule, statusFilter, searchQuery]);

  // Paginated data
  const paginatedValidations = useMemo(() => {
    return filteredValidations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredValidations, page, rowsPerPage]);

  // Calculate module statistics
  const moduleStats = useMemo(() => {
    const stats = {};
    MODULES.forEach(module => {
      const moduleValidations = module.id === 0 
        ? allValidations 
        : allValidations.filter(v => v.module === module.id);
      
      stats[module.id] = {
        total: moduleValidations.length,
        pass: moduleValidations.filter(v => v.status === STATUS.PASS).length,
        fail: moduleValidations.filter(v => v.status === STATUS.FAIL).length,
        running: moduleValidations.filter(v => v.status === STATUS.RUNNING).length,
        notApplicable: moduleValidations.filter(v => v.status === STATUS.NOT_APPLICABLE).length,
      };
    });
    return stats;
  }, [allValidations]);

  return (
    <ErrorBoundary>
      <Box className="rtv-home-container">
        {/* Animated Background Particles */}
        <div className="rtv-particles">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="rtv-particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${15 + Math.random() * 10}s`,
              }}
            />
          ))}
        </div>

        <Box className="rtv-content">
          {/* Module Tabs */}
          <Box className="rtv-module-tabs">
            <Tabs
              value={selectedModule}
              onChange={handleModuleChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="module tabs"
            >
              {MODULES.map((module) => (
                <Tab
                  key={module.id}
                  label={
                    <Box className="rtv-tab-label">
                      <span className="rtv-tab-icon">{module.icon}</span>
                      <span>{module.name}</span>
                    </Box>
                  }
                  value={module.id}
                />
              ))}
            </Tabs>
          </Box>



          {/* Toolbar with Search and Filters */}
          <Box className="rtv-toolbar">
            <TextField
              size="small"
              placeholder="Search validations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="rtv-search-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Box className="rtv-toolbar-actions">
              <Button
                variant="contained"
                size="small"
                className="rtv-action-btn rtv-details-btn"
                onClick={() => {
                  const filters = ['all', 'pass', 'fail', 'running'];
                  const currentIndex = filters.indexOf(statusFilter);
                  setStatusFilter(filters[(currentIndex + 1) % filters.length]);
                }}
                startIcon={<FilterListIcon />}
              >
                {statusFilter === 'all' ? 'All' : statusFilter}
              </Button>
            </Box>
          </Box>

          {/* Validations Table */}
          <Box className="rtv-validations-section">
            {filteredValidations.length > 0 ? (
              <TableContainer component={Paper} className="rtv-table-container">
                <Table className="rtv-table">
                  <TableHead>
                    <TableRow className="rtv-table-header-row">
                      <TableCell className="rtv-table-header-cell">Validation Check</TableCell>
                      <TableCell className="rtv-table-header-cell" align="center">Status</TableCell>
                      <TableCell className="rtv-table-header-cell" align="center">Last Run</TableCell>
                      <TableCell className="rtv-table-header-cell" align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedValidations.map((validation) => {
                      const isNotApplicable = validation.status === STATUS.NOT_APPLICABLE;
                      return (
                        <TableRow key={validation.id} className="rtv-table-row">
                          <TableCell className="rtv-table-cell">
                            <Typography variant="body2" className="rtv-cell-text">
                              {validation.checkName}
                            </Typography>
                          </TableCell>
                          <TableCell className="rtv-table-cell" align="center">
                            <Chip
                              label={validation.status || 'Unknown'}
                              size="small"
                              className={`rtv-status-chip rtv-status-${validation.status?.toLowerCase().replace(/ /g, '-')}`}
                            />
                          </TableCell>
                          <TableCell className="rtv-table-cell" align="center">
                            <Typography variant="body2" className="rtv-cell-text">
                              {validation.lastRun ? format(new Date(validation.lastRun), 'MMM dd, yyyy') : 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell className="rtv-table-cell" align="center">
                            <Box className="rtv-action-buttons">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  className="rtv-action-btn rtv-details-btn"
                                  onClick={() => handleOpenDetails(validation)}
                                  disabled={isNotApplicable}
                                >
                                  Details
                                </Button>
                              </span>
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  className="rtv-action-btn rtv-details-btn"
                                  disabled={isNotApplicable}
                                  onClick={async () => {
                                    try {
                                      const documents = await fetchDocuments(validation.transactionId);
                                      navigate('/document-manager', {
                                        state: {
                                          transactionId: validation.transactionId,
                                          transactionName: validation.transactionName,
                                          fromRuntimeValidation: true,
                                          validations: allValidations,
                                          module: selectedModule,
                                          documents,
                                          validationId: validation.id,
                                        },
                                      });
                                    } catch (error) {
                                      alert(error.message);
                                    }
                                  }}
                                >
                                  Documents
                                </Button>
                              </span>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredValidations.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  className="rtv-table-pagination"
                />
              </TableContainer>
            ) : (
              <Box className="rtv-no-data">
                <Typography variant="h6">No validations available</Typography>
                <Typography variant="body2">
                  {searchQuery
                    ? 'Try adjusting your search or filters'
                    : 'No validations found for this module'}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Details Modal */}
          <Dialog
            open={openDetailsModal}
            onClose={handleCloseDetails}
            maxWidth="md"
            fullWidth
            classes={{ paper: 'rtv-dialog' }}
          >
            <DialogTitle className="rtv-dialog-title">
              {selectedValidation?.checkName || 'Validation Details'}
            </DialogTitle>
            <DialogContent className="rtv-dialog-content">
              <Box className="rtv-detail-row">
                <Typography variant="subtitle2">Status:</Typography>
                <Chip
                  label={selectedValidation?.status || 'Unknown'}
                  size="small"
                  className={`rtv-status-chip rtv-status-${selectedValidation?.status?.toLowerCase().replace(/ /g, '-')}`}
                />
              </Box>
              {selectedValidation?.details && (
                <Box className="rtv-detail-row">
                  <Typography variant="subtitle2">Details:</Typography>
                  <Typography variant="body2">{selectedValidation.details}</Typography>
                </Box>
              )}
              {selectedValidation?.status !== STATUS.PASS && selectedValidation?.evidence && (
                <Box className="rtv-detail-row">
                  <Typography variant="subtitle2">Evidence:</Typography>
                  <Typography variant="body2">{selectedValidation.evidence}</Typography>
                </Box>
              )}
              <Box className="rtv-detail-row">
                <Typography variant="subtitle2">Last Run:</Typography>
                <Typography variant="body2">
                  {selectedValidation?.lastRun || 'Unknown'}
                </Typography>
              </Box>
              {selectedValidation?.changeHistory?.length > 0 && (
                <Box className="rtv-change-history">
                  <Typography variant="subtitle2">
                    Change History:
                  </Typography>
                  {selectedValidation.changeHistory.map((change, index) => (
                    <Box key={index} className="rtv-change-item">
                      <Typography variant="body2">
                        <strong>{change.field}</strong>: "{change.oldValue}" → "
                        {change.newValue}"
                      </Typography>
                      <Typography variant="caption">
                        {change.timestamp}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions className="rtv-dialog-actions">
              <Button onClick={handleCloseDetails} variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default RuntimeValidation;